import { api } from "data";
import { logError } from "utils/error";
import {
  containsOnlySpaces,
  isAlphaNumeric,
  isValidGst,
  isValidPan,
} from "utils/validations";
import {
  BANK_DETAILS_VALIDATION_MESSAGES,
  BANK_DETAILS_VALIDATION_CONFIG,
  BANK_DETAILS_MESSAGES,
  PAN_DETAILS_MESSAGES,
  BANK_DETAILS_STORAGE_KEYS,
  KYC_VERIFICATION_MESSAGES,
  KYC_REFRESH_MESSAGES,
  GST_DETAILS_MESSAGES,
  GST_LIST_DETAILS_MESSAGES,
  GST_DETAIL_FIELDS,
} from "./constants";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { checkIsArrayEmpty } from "@my-scoot/exly-react-component-lib";

export const emptySpace = (value) => {
  if (!checkIsArrayEmpty(value) && containsOnlySpaces(value))
    return BANK_DETAILS_VALIDATION_MESSAGES.INVALID;
};

export const checkAccountLength = (value) => {
  if (`${value}`.length > BANK_DETAILS_VALIDATION_CONFIG.ACCOUNT_MAX_LENGTH)
    return BANK_DETAILS_VALIDATION_MESSAGES.ACCOUNT_MAX_LENGTH;
  if (`${value}`.length < BANK_DETAILS_VALIDATION_CONFIG.ACCOUNT_MIN_LENGTH)
    return BANK_DETAILS_VALIDATION_MESSAGES.ACCOUNT_MIN_LENGTH;
};

export const validatePincode = (value) => {
  const check = BANK_DETAILS_VALIDATION_CONFIG.PINCODE_REGEX.test(value);
  if (!check) return BANK_DETAILS_VALIDATION_MESSAGES.INVALID_PINCODE;
};

export const checkIFSC = (value) => {
  if (value.includes(" ") || !isAlphaNumeric(value))
    return BANK_DETAILS_VALIDATION_MESSAGES.INVALID_IFSC;
  if (value.length !== BANK_DETAILS_VALIDATION_CONFIG.IFSC_LENGTH)
    return BANK_DETAILS_VALIDATION_MESSAGES.IFSC_LENGTH;
};

export const checkPan = (value) => {
  if (!checkIsArrayEmpty(value) && !isValidPan(value))
    return BANK_DETAILS_VALIDATION_MESSAGES.INVALID_PAN;
};

export const checkGst = (value) => {
  if (!isValidGst(value)) return BANK_DETAILS_VALIDATION_MESSAGES.INVALID_GST;
};

export const useBankDetails = ({
  set_payout_data,
  set_access,
  set_gst,
  set_gst_status,
  setGetGSTInfo,
  setIsBillingStateMandatory,
}) => {
  const { refetch: loadUserBankDetails } = useCustomQuery({
    url: api.user_payout_details,
    queryKey: [BANK_DETAILS_STORAGE_KEYS.GST_STATUS],
    enabled: false,
    onSuccess: (account_details_response) => {
      const account_details = account_details_response?.account_details;
      set_payout_data(account_details);

      if (!checkIsArrayEmpty(account_details.account_number)) {
        set_access(true);
      }

      set_gst(!checkIsArrayEmpty(account_details.gst_info));
      set_gst_status(account_details.gst_status);
      setGetGSTInfo(account_details?.get_gst_info);

      setIsBillingStateMandatory(!account_details?.billing_state_non_mandatory);

      window.localStorage.setItem(
        BANK_DETAILS_STORAGE_KEYS.GST_STATUS,
        account_details.gst_status
      );
    },
    onError: (err) => {
      logError({
        error: err,
        when: BANK_DETAILS_MESSAGES.ERROR_WHEN,
        occuredAt: BANK_DETAILS_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const { mutateAsync: handeSaveHelper } = useCustomMutation({
    url: api.user_payout_details,
    onError: (err) => {
      logError({
        error: err,
        when: BANK_DETAILS_MESSAGES.ERROR_WHEN,
        occuredAt: BANK_DETAILS_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const { mutateAsync: handlePANSaveHelper } = useCustomMutation({
    url: api.user_pan_details,
    onError: (err) => {
      logError({
        error: err,
        when: PAN_DETAILS_MESSAGES.ERROR_WHEN,
        occuredAt: PAN_DETAILS_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const { mutateAsync: handeGstSaveHelper } = useCustomMutation({
    url: api.user_gst_details,
    returnFullResponse: true,
    onError: (err) => {
      logError({
        error: err,
        when: GST_DETAILS_MESSAGES.ERROR_WHEN,
        occuredAt: GST_DETAILS_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const { refetch: refetchGstList } = useCustomQuery({
    url: api.user_gst_list_details,
    queryKey: [GST_DETAIL_FIELDS.gstQueryKey],
    enabled: false,
    onError: (err) => {
      logError({
        error: err,
        when: GST_LIST_DETAILS_MESSAGES.ERROR_WHEN,
        occuredAt: GST_LIST_DETAILS_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const handeGstListHelper = async () => {
    const result = await refetchGstList();
    return result.data;
  };

  const { mutateAsync: handleKYCVerificationHelper } = useCustomMutation({
    url: api.user_kyc_initiate,
    onError: (err) => {
      logError({
        error: err,
        when: KYC_VERIFICATION_MESSAGES.ERROR_WHEN,
        occuredAt: KYC_VERIFICATION_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  const { mutateAsync: handleKYCRefreshHelper } = useCustomMutation({
    url: api.user_kyc_refresh,
    onError: (err) => {
      logError({
        error: err,
        when: KYC_REFRESH_MESSAGES.ERROR_WHEN,
        occuredAt: KYC_REFRESH_MESSAGES.ERROR_OCCURRED_AT,
      });
    },
  });

  return {
    loadUserBankDetails,
    handeSaveHelper,
    handlePANSaveHelper,
    handeGstSaveHelper,
    handeGstListHelper,
    handleKYCVerificationHelper,
    handleKYCRefreshHelper,
  };
};
